import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import styled from 'styled-components';
import FileFormatter from 'components/FileFormatter';
import TagColorFormatter from 'components/TagColorFormatter';
import { TagFormatter } from 'components/TagFormatter';
import TextInput from 'components/TextInput';
import Button from 'components/Button';
import PageHeader from 'components/styled/PageHeader';
import Text from 'components/styled/Text';
import { BiTrashAlt, BiCopy } from 'react-icons/bi';
import { BsPlus, BsFlag } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import {
  colors,
  FILE_TYPES,
  PAGE_PATHS,
  PRICE_TYPES,
  ROLES,
} from '../../constants';
import { debounced, getColumnsWidth } from '../../utils/helpers';
import AddToReportModal from 'components/modals/AddToReportModal';
import { useTranslation } from 'react-i18next';
import {
  ActionBar,
  ActionBarButton,
  ActionBarButtons,
} from 'components/styled/ActionBar';
import { GridTable } from 'components/GridTable';
import { useReduxActions } from 'hooks/useReduxActions';
import { useSelector } from 'react-redux';
import {
  categoriesSelector,
  isDraftExistsSelector,
  fixProductsSelector,
  flexProductsSelector,
  userSelector,
  productValidationErrorsSelector,
} from 'store/selectors';
import DropDown from 'components/DropDown';
import PageSearchContainer from 'components/styled/PageSearchContainer';
import EmptyCell from 'components/EmptyCell';

const StyledNameVariant = styled.div`
  padding-left: 3px;
  border-left: ${props => (props.isVariant ? '4px solid' : 'none')};
  border-color: ${colors.green};
`;

const TabContainer = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  padding-left: 32px;
  border-bottom: 1px solid ${colors.grey90};
`;

const Tab = styled(Text)`
  min-width: 163.25px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-bottom: ${props =>
    props.color === colors.green && '2px solid' + colors.green};
  cursor: pointer;
`;

const Product = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const gridRef = useRef(null);
  const actions = useReduxActions();
  const isDraftExists = useSelector(isDraftExistsSelector);
  const fixProducts = useSelector(fixProductsSelector);
  const flexProducts = useSelector(flexProductsSelector);
  const user = useSelector(userSelector);
  const categories = useSelector(categoriesSelector);
  const productValidationErrors = useSelector(productValidationErrorsSelector);

  const [addToReportIsOpen, setAddToReportIsOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isEditable, setIsEditable] = useState(true);
  const [currentOptionsDropDownIndex, setCurrentOptionsDropDownIndex] =
    useState({});
  const [currentTab, setCurrentTab] = useState(PRICE_TYPES.FLEX);

  const columnsWidth = getColumnsWidth();
  const columnDefs = useMemo(
    () => [
      {
        field: 'select',
        maxWidth: 66,
        width: columnsWidth.select,
        pinned: 'left',
        headerCheckboxSelection: true,
        checkboxSelection: true,
      },
      {
        headerName: t('product.header.id'),
        maxWidth: 75,
        field: 'index',
        width: columnsWidth.index,
        pinned: 'left',
      },
      {
        headerName: t('product.header.code.position'),
        field: 'code_position',
        width: columnsWidth.code_position,
        pinned: 'left',
        minWidth: 150,
      },
      {
        headerName: t('product.header.product.name'),
        field: 'product_name',
        minWidth: 150,
        width: columnsWidth.product_name,
        pinned: 'left',
        editable: isEditable,
        cellRenderer: props => {
          const { product_name, isVariant } = props.data;
          return (
            <StyledNameVariant isVariant={isVariant}>
              {product_name}
            </StyledNameVariant>
          );
        },
      },
      {
        headerName: t('product.header.category'),
        field: 'category',
        minWidth: 150,
        width: columnsWidth.category,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          overflow: 'visible',
        },
        cellRenderer: props => {
          return (
            <DropDown
              direction={props.rowIndex > 3 ? 'bottom' : 'top'}
              onOutsideClick={handleOpenOptionsDropDown(
                props.rowIndex,
                props.colDef.field,
              )}
              show={
                props.rowIndex ===
                currentOptionsDropDownIndex[props.colDef.field]
              }
              right={-60}
              items={categories.map(item => ({
                text: item.name,
                onClick: handleSelectOption(item, props),
              }))}>
              {props?.value?.name ? (
                <Text
                  variant="body-small"
                  onClick={handleOpenOptionsDropDown(
                    props.rowIndex,
                    props.colDef.field,
                  )}>
                  {props?.value?.name}
                </Text>
              ) : (
                <EmptyCell
                  onClick={handleOpenOptionsDropDown(
                    props.rowIndex,
                    props.colDef.field,
                  )}
                />
              )}
            </DropDown>
          );
        },
      },
      {
        headerName: t('product.header.subcategory'),
        field: 'subcategory',
        minWidth: 150,
        width: columnsWidth.subcategory,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          overflow: 'visible',
        },
        cellRenderer: props => {
          const category = categories.find(
            c => c.name === props.data.category?.name,
          );
          return (
            <DropDown
              direction={props.rowIndex > 3 ? 'bottom' : 'top'}
              show={
                props.rowIndex ===
                currentOptionsDropDownIndex[props.colDef.field]
              }
              onOutsideClick={handleOpenOptionsDropDown(
                props.rowIndex,
                props.colDef.field,
              )}
              items={
                category?.subcategory.map(item => ({
                  text: item.name,
                  onClick: handleSelectOption(item.name, props),
                })) || []
              }>
              {props?.value ? (
                <Text
                  variant="body-small"
                  onClick={handleOpenOptionsDropDown(
                    props.rowIndex,
                    props.colDef.field,
                  )}>
                  {props?.value}
                </Text>
              ) : (
                <EmptyCell
                  onClick={handleOpenOptionsDropDown(
                    props.rowIndex,
                    props.colDef.field,
                  )}
                />
              )}
            </DropDown>
          );
        },
      },
      {
        headerName: t('product.header.link'),
        field: 'link',
        width: columnsWidth.link,
        type: 'link',
        editable: isEditable,
      },
      {
        headerName: t('product.header.photo.video'),
        field: 'photo_video',
        width: columnsWidth.photo_video,
        minWidth: 150,
        cellRenderer: props => (
          <FileFormatter
            data={props.data.photo_video}
            editable={isEditable}
            type={FILE_TYPES.IMAGE_AND_VIDEO}
            rowId={props.data.row_id}
            recall={reFetch}
            model="product"
          />
        ),
      },
      {
        headerName: t('product.header.color'),
        field: 'color',
        width: columnsWidth.color,
        minWidth: 250,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          overflow: 'visible',
        },
        cellRenderer: props => (
          <TagColorFormatter
            rowIndex={props.rowIndex}
            data={props.data}
            editable={isEditable}
            onChangeRow={handleUpdateProduct}
          />
        ),
      },
      {
        headerName: t('product.header.product.size'),
        field: 'product_size',
        width: columnsWidth.product_size,
        editable: isEditable,
        type: 'tags',
        cellRenderer: props => (
          <TagFormatter
            data={props}
            field={props.colDef.field}
            editable={isEditable}
            onDeleteTag={handleUpdateProduct}
          />
        ),
      },
      {
        headerName: t('product.header.product.weight'),
        field: 'product_weight',
        width: columnsWidth.product_weight,
        type: 'weight',
        minWidth: 150,
        editable: isEditable,
      },
      {
        headerName: t('product.header.description'),
        field: 'description',
        width: columnsWidth.description,
        minWidth: 150,
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
        editable: isEditable,
      },
      {
        headerName: t('product.header.EXW'),
        field: 'EXW',
        minWidth: 120,
        width: columnsWidth.EXW,
        type: 'chinaCurrency',
        editable: isEditable,
        hide:
          user.role === ROLES.AGENT ||
          user.role === ROLES.HELPER ||
          currentTab === PRICE_TYPES.FLEX,
      },
      {
        headerName: t('product.header.MOQ'),
        field: 'MOQ',
        width: columnsWidth.MOQ,
        minWidth: 120,
        editable: isEditable,
      },
      {
        headerName: t('product.header.price'),
        field: 'price',
        width: columnsWidth.price,
        type: 'USDTCurrency',
        minWidth: 150,
        editable: isEditable,
        hide: currentTab === PRICE_TYPES.FIX,
      },
      {
        headerName: t('product.header.quantity'),
        field: 'quantity',
        width: columnsWidth.quantity,
        minWidth: 120,
        editable: isEditable,
        hide: currentTab === PRICE_TYPES.FIX,
      },
      {
        headerName: t('product.header.10pcs'),
        field: '10pcs',
        width: columnsWidth['10pcs'],
        type: 'USDTCurrency',
        editable: isEditable,
        hide: currentTab === PRICE_TYPES.FLEX,
      },
      {
        headerName: t('product.header.50pcs'),
        field: '50pcs',
        width: columnsWidth['50pcs'],
        type: 'USDTCurrency',
        editable: isEditable,
        hide: currentTab === PRICE_TYPES.FLEX,
      },
      {
        headerName: t('product.header.100pcs'),
        field: '100pcs',
        width: columnsWidth['100pcs'],
        type: 'USDTCurrency',
        editable: isEditable,
        hide: currentTab === PRICE_TYPES.FLEX,
      },
      {
        headerName: t('product.header.500pcs'),
        field: '500pcs',
        width: columnsWidth['500pcs'],
        type: 'USDTCurrency',
        editable: isEditable,
        hide: currentTab === PRICE_TYPES.FLEX,
      },
      {
        headerName: t('product.header.box.size'),
        field: 'box_size',
        width: columnsWidth.box_size,
        editable: isEditable,
        type: ['tags', 'box'],
        cellRenderer: props => (
          <TagFormatter
            data={props}
            field={props.colDef.field}
            editable={isEditable}
            onDeleteTag={handleUpdateProduct}
          />
        ),
      },
      {
        headerName: t('product.header.box.weight'),
        field: 'box_weight',
        width: columnsWidth.box_weight,
        editable: isEditable,
        type: ['box', 'weight'],
        minWidth: 150,
      },
    ],
    [isEditable, categories, currentOptionsDropDownIndex, currentTab, user], // eslint-disable-line
  );

  const reFetch = () => {
    if (currentTab === PRICE_TYPES.FLEX) {
      actions.getAllFlexProducts();
    } else {
      actions.getAllFixProducts();
    }
  };

  const redirectToDraft = () => {
    navigate(PAGE_PATHS.PRODUCT_DRAFT);
  };

  const handleChangeSearch = debounced(event => {
    actions.getProductsBySearch(event.target.value, currentTab);
  });

  const handleAddToReport = () => {
    setAddToReportIsOpen(true);
  };

  const handleOpenOptionsDropDown = (index, field) => () => {
    if (currentOptionsDropDownIndex[field] === index) {
      setCurrentOptionsDropDownIndex({ [field]: null });
    } else {
      setCurrentOptionsDropDownIndex({ [field]: index });
    }
  };

  const handleSelectOption = (item, props) => () => {
    const newData = { ...props.data, [props.colDef.field]: item };
    actions.updateProduct(props.data._id, newData, currentTab);
    setCurrentOptionsDropDownIndex({ [props.colDef.field]: null });
  };

  const handleChangeProduct = useCallback((_, cell) => {
    if (cell.colDef.field === 'EXW') {
      cell.data['10pcs'] = (cell.data.EXW * 1.28) / 6.66;
      cell.data['50pcs'] = (cell.data.EXW * 1.2) / 6.66;
      cell.data['100pcs'] = (cell.data.EXW * 1.18) / 6.66;
      cell.data['500pcs'] = (cell.data.EXW * 1.13) / 6.66;
    }
    actions.changeProduct(cell.data._id, cell.data);
  }, []); // eslint-disable-line

  const handleUpdateProduct = cell => {
    actions.updateProduct(cell._id, cell, currentTab);
  };

  const handleCloseModal = () => {
    setAddToReportIsOpen(false);
  };

  const handleClickTab = tab => () => {
    setCurrentTab(tab.id);
  };

  const onSelectionChanged = useCallback(() => {
    setSelectedRows(gridRef.current.api.getSelectedRows());
  }, []);

  useEffect(() => {
    setIsEditable(user.role !== ROLES.AGENT && user.role !== ROLES.HELPER);
  }, [user]);

  useEffect(() => {
    actions.getAllFlexProducts();
    actions.getAllFixProducts();
    actions.getProductIsDraftExists();
    actions.getAllCategories();
  }, []); // eslint-disable-line

  const TABS = [
    { id: PRICE_TYPES.FLEX, label: t('product.tabs.flex') },
    { id: PRICE_TYPES.FIX, label: t('product.tabs.fix') },
  ];

  return (
    <>
      <PageHeader size="large">
        <Text variant="title2">{t('product.title')}</Text>
        <PageSearchContainer>
          {user.role !== ROLES.AGENT &&
            user.role !== ROLES.HELPER &&
            !isDraftExists && (
              <Button size="medium" onClick={redirectToDraft} variant="primary">
                {t('product.add.products.button')}
              </Button>
            )}
          <TextInput
            type="text"
            placeholder={t('product.input.placeholder')}
            onChange={handleChangeSearch}
            size="medium"
          />
        </PageSearchContainer>
      </PageHeader>
      <TabContainer>
        {TABS.map(tab => (
          <Tab
            key={tab.id}
            color={tab.id === currentTab ? colors.green : colors.navy}
            variant="body-bold"
            onClick={handleClickTab(tab)}>
            {tab.label}
          </Tab>
        ))}
      </TabContainer>
      {user.role !== ROLES.AGENT &&
        user.role !== ROLES.HELPER &&
        isDraftExists &&
        !selectedRows.length && (
          <ActionBar>
            <Text variant="body" color={colors.black}>
              {t('product.draft.bar.label')}
            </Text>
            <ActionBarButtons>
              <Button
                variant="secondary"
                size="small"
                onClick={redirectToDraft}>
                {t('product.draft.bar.edit.button')}
              </Button>
              <BiTrashAlt fill={colors.red} />
            </ActionBarButtons>
          </ActionBar>
        )}
      {!!selectedRows.length && (
        <ActionBar>
          <ActionBarButtons>
            <ActionBarButton onClick={handleAddToReport}>
              <BsPlus fill={colors.grey40} />
              <Text variant="body" color={colors.navy}>
                {t('product.action.bar.report.button')}
              </Text>
            </ActionBarButton>
            <ActionBarButton>
              <BiCopy fill={colors.grey40} />
              <Text variant="body" color={colors.navy}>
                {t('product.action.bar.duplicate.button')}
              </Text>
            </ActionBarButton>
          </ActionBarButtons>
          <ActionBarButtons>
            <ActionBarButton>
              <BsFlag fill={colors.grey40} />
              <Text variant="body" color={colors.navy}>
                {t('product.action.bar.flag.button')}
              </Text>
            </ActionBarButton>
            <ActionBarButton>
              <BiTrashAlt fill={colors.red} />
              <Text variant="body" color={colors.red}>
                {t('product.action.bar.delete.button')}
              </Text>
            </ActionBarButton>
          </ActionBarButtons>
        </ActionBar>
      )}
      {addToReportIsOpen && (
        <AddToReportModal
          isOpen={addToReportIsOpen}
          isChosen={gridRef}
          onClose={handleCloseModal}
        />
      )}
      <GridTable
        ref={gridRef}
        rows={currentTab === PRICE_TYPES.FLEX ? flexProducts : fixProducts}
        errors={productValidationErrors}
        columnDefs={columnDefs}
        onSelectItem={onSelectionChanged}
        onUpdateItem={handleChangeProduct}
        rowSelection="multiple"
      />
    </>
  );
};

export default Product;
